import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, AlertCircle, CheckCircle2, Clock, Phone, FileText, Stethoscope, Users, Building2, Hospital, Lock, Globe, Zap, HeartPulse, Briefcase } from "lucide-react";

const ERGO_COLOR = "#E2001A";

const ErgoTarif = () => {
  const heroFeatures = [
    { icon: Phone, title: "24/7 Cyber-Hotline", subtitle: "Rund um die Uhr" },
    { icon: Shield, title: "Rundumschutz", subtitle: "Alle Cyber-Risiken" },
    { icon: Globe, title: "Prävention inklusive", subtitle: "Cyber-Schulungen" }
  ];

  const coverageAreas = [
    {
      icon: Zap,
      title: "Cyber-Notfallhilfe 24/7",
      items: [
        "Sofortige Hilfe bei Cyber-Angriffen",
        "IT-Forensik und Schadensanalyse",
        "Koordination von IT-Spezialisten",
        "Krisenmanagement und PR-Beratung",
        "Wiederherstellung der IT-Systeme"
      ]
    },
    {
      icon: Clock,
      title: "Betriebsunterbrechung & Ertragsausfall",
      items: [
        "Ersatz des entgangenen Gewinns",
        "Deckung fortlaufender Kosten",
        "Mehrkosten für Notbetrieb",
        "Kosten für Ersatzgeräte",
        "Keine Wartezeit bei Schadenseintritt"
      ]
    },
    {
      icon: FileText,
      title: "Datenrettung & IT-Forensik",
      items: [
        "Professionelle Datenwiederherstellung",
        "IT-forensische Untersuchungen",
        "Wiederherstellung von Systemen",
        "Backup-Wiederaufbau",
        "Rekonstruktion verlorener Daten"
      ]
    },
    {
      icon: Lock,
      title: "Cyber-Haftpflicht & DSGVO",
      items: [
        "DSGVO-Bußgelder und Schadensersatz",
        "Rechtsanwalts- und Gerichtskosten",
        "Schadensersatzforderungen Dritter",
        "Cyber-Erpressungsschutz",
        "Kommunikationskosten bei Datenpannen"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Arztpraxen aller Fachrichtungen",
      description: "Hausärzte, Fachärzte, Gemeinschaftspraxen und Einzelpraxen mit digitalen Patientendaten",
      benefits: [
        "24/7-Notfall-Hotline für Cyber-Vorfälle",
        "Schutz sensibler Gesundheitsdaten",
        "DSGVO-konforme Schadensabwicklung",
        "Betriebsunterbrechungsschutz inklusive"
      ],
      recommendation: "Empfohlene Deckung: 100.000 - 300.000 €"
    },
    {
      icon: HeartPulse,
      title: "MVZ & Medizinische Zentren",
      description: "Medizinische Versorgungszentren mit komplexer IT-Infrastruktur und mehreren Standorten",
      benefits: [
        "Schutz vernetzter IT-Systeme",
        "Umfassende Betriebsunterbrechung",
        "Forensische Schadensanalyse",
        "Multi-Standort-Absicherung"
      ],
      recommendation: "Empfohlene Deckung: 300.000 - 1.000.000 €"
    },
    {
      icon: Building2,
      title: "Kliniken & Krankenhäuser",
      description: "Große medizinische Einrichtungen mit kritischer IT-Infrastruktur",
      benefits: [
        "Schutz medizinischer Geräte und Systeme",
        "Notfallmanagement für kritische Infrastruktur",
        "Umfassende Haftpflichtdeckung",
        "Individuelle Versicherungslösungen"
      ],
      recommendation: "Empfohlene Deckung: 1.000.000 - 5.000.000 €"
    },
    {
      icon: Briefcase,
      title: "Therapie- & Heilberufe",
      description: "Physiotherapie, Ergotherapie, Logopädie und psychotherapeutische Praxen",
      benefits: [
        "Günstige Prämien für kleine Praxen",
        "Schutz digitaler Behandlungspläne",
        "Präventive Mitarbeiterschulungen",
        "Schnelle Schadensregulierung"
      ],
      recommendation: "Empfohlene Deckung: 50.000 - 150.000 €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Versicherungssummen",
      description: "Flexibel wählbar von 50.000 EUR bis 5 Mio. EUR - individuell anpassbar an Ihre Praxisgröße"
    },
    {
      icon: FileText,
      title: "Selbstbeteiligung",
      description: "Wahlweise 0 EUR, 500 EUR oder 1.000 EUR Selbstbeteiligung für optimale Prämiengestaltung"
    },
    {
      icon: CheckCircle2,
      title: "Sofortiger Schutz",
      description: "Keine Wartezeiten - Versicherungsschutz und 24/7-Hotline ab Tag 1 verfügbar"
    },
    {
      icon: Clock,
      title: "Vertragslaufzeit",
      description: "1 Jahr Laufzeit mit automatischer Verlängerung, 3 Monate Kündigungsfrist"
    },
    {
      icon: Globe,
      title: "Präventionsleistungen",
      description: "Cyber-Awareness-Schulungen, Phishing-Tests und Sicherheitsberatung inklusive"
    },
    {
      icon: Users,
      title: "Umfassender Schutz",
      description: "Gilt für alle Mitarbeiter, mobile Geräte und externe Dienstleister"
    }
  ];

  const faqs = [
    {
      question: "Was leistet die ERGO Cyberversicherung?",
      answer: "Die ERGO Cyberversicherung bietet umfassenden Schutz bei Cyber-Vorfällen: 24/7-Notfallhilfe, IT-Forensik, Datenwiederherstellung, Betriebsunterbrechung, Cyber-Erpressung, DSGVO-Bußgelder und Cyber-Haftpflicht. Zusätzlich sind Präventionsleistungen wie Mitarbeiterschulungen enthalten."
    },
    {
      question: "Für wen eignet sich die ERGO Cyberversicherung?",
      answer: "Die Versicherung ist ideal für Arztpraxen, MVZ, Kliniken, Therapiepraxen und alle Heilberufe, die digitale Patientendaten verarbeiten. Durch flexible Versicherungssummen ist sie für kleine Einzelpraxen bis große Kliniken geeignet."
    },
    {
      question: "Wie hoch sollte die Versicherungssumme sein?",
      answer: "Die Versicherungssumme richtet sich nach Praxisgröße und Risikoprofil: Kleine Praxen 50.000-150.000 EUR, mittlere Praxen 150.000-300.000 EUR, MVZ 300.000-1.000.000 EUR, Kliniken 1-5 Mio. EUR."
    },
    {
      question: "Was kostet die ERGO Cyberversicherung?",
      answer: "Die Beitragshöhe hängt von mehreren Faktoren ab: Fachrichtung, Praxisgröße, Umsatz, vorhandene IT-Sicherheitsmaßnahmen und gewählte Versicherungssumme. Für ein individuelles Angebot kontaktieren Sie uns gerne für eine kostenlose Beratung."
    },
    {
      question: "Gibt es Wartezeiten bei der ERGO Cyberversicherung?",
      answer: "Nein, es gibt keine Wartezeiten. Der vollständige Versicherungsschutz beginnt sofort nach Vertragsabschluss. Die 24/7-Cyber-Notfall-Hotline steht ab dem ersten Versicherungstag zur Verfügung."
    },
    {
      question: "Sind DSGVO-Bußgelder versichert?",
      answer: "Ja, die ERGO Cyberversicherung deckt DSGVO-Bußgelder ab, soweit diese rechtlich versicherbar sind. Ebenso sind Kosten für Datenschutzbeauftragte, Meldungen an Behörden und Benachrichtigungen Betroffener enthalten."
    },
    {
      question: "Wie funktioniert die 24/7-Cyber-Notfall-Hotline?",
      answer: "Bei einem Cyber-Vorfall steht die Hotline rund um die Uhr zur Verfügung. Sie erhalten sofortigen Zugang zu IT-Spezialisten, die den Angriff stoppen, Schäden analysieren und die Wiederherstellung koordinieren."
    },
    {
      question: "Sind auch Cyber-Erpressungen versichert?",
      answer: "Ja, die Versicherung deckt Cyber-Erpressungen ab, einschließlich Lösegeldzahlungen (soweit rechtlich zulässig), Verhandlungskosten mit Erpressern und Kosten für spezialisierte Cyber-Sicherheitsexperten."
    },
    {
      question: "Welche Präventionsleistungen sind enthalten?",
      answer: "Die ERGO bietet Cyber-Awareness-Schulungen für Mitarbeiter, regelmäßige Phishing-Simulationen, IT-Sicherheitsberatung und Online-Trainings zur Sensibilisierung für Cyber-Risiken."
    },
    {
      question: "Was passiert bei einem Datenleck?",
      answer: "Die Versicherung übernimmt alle Kosten: Benachrichtigung betroffener Patienten, Meldung an Datenschutzbehörden, PR-Krisenmanagement, Rechtsberatung, Schadensersatzforderungen und eventuelle DSGVO-Bußgelder."
    }
  ];

  const documents = [
    {
      title: "Produktinformationsblatt Cyberversicherung",
      type: "PDF",
      url: "https://www.ergo.de/de/downloads/cyber-pib.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF",
      url: "https://www.ergo.de/de/downloads/cyber-avb.pdf"
    }
  ];

  return (
    <BaseTariffPage
      companyName="ERGO"
      companyColor={ERGO_COLOR}
      productRating={{ value: 4.7, count: 521 }}
      priceRange="60-220"
      pageTitle="ERGO Cyberversicherung für Arztpraxen - Umfassender Rundumschutz"
      metaDescription="Die ERGO Cyberversicherung bietet Arztpraxen, MVZ und Kliniken umfassenden Schutz vor Cyber-Risiken mit 24/7-Notfall-Hotline, DSGVO-Schutz und Präventionsleistungen."
      metaKeywords="ERGO Cyberversicherung, Cyber-Versicherung Arztpraxis, ERGO Cyber-Schutz, Arztpraxis Cybersicherheit"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/ergo"
      heroLabel="Traditionsversicherer mit modernem Cyber-Schutz"
      heroTitle={<>ERGO Cyberversicherung<br />für medizinische Einrichtungen</>}
      heroDescription="Starker Rundumschutz für Arztpraxen, MVZ und Kliniken mit 24/7-Cyber-Notfallhilfe, umfassender Betriebsunterbrechungsdeckung und Präventionsleistungen. Von einem der größten deutschen Versicherer."
      heroFeatures={heroFeatures}
      alertTitle="Warum ERGO Cyberversicherung?"
      alertDescription="Cyber-Angriffe auf Gesundheitseinrichtungen nehmen dramatisch zu - 2023 war jede dritte Arztpraxis betroffen. Die ERGO Cyberversicherung schützt Sie mit sofortiger 24/7-Hilfe, umfassenden Leistungen und über 120 Jahren Versicherungserfahrung."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
    />
  );
};

export default ErgoTarif;
